import { CSSProperties, useEffect, useState } from 'react';
import { TabComponent, TabsComponent } from '@vivli/shared/components';
import { useActiveUserContext, useAssignedAppTypeContext } from '@vivli/core/infrastructure/context';
import { StudyStatusEnum, StudyTabEnum } from '@vivli/features/studies/infrastructure/enum';
import { IStudy } from '@vivli/features/studies/infrastructure/interface';
import { GridApi } from 'ag-grid-community';
import 'react-slidedown/lib/slidedown.css';
import './studies.style.scss';
import { StudiesGridComponent } from '@vivli/features/studies/components';
import { useStudies } from '@vivli/features/studies/infrastructure/hook';
import { AssignedAppTypeEnum, PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { useTranslation } from 'react-i18next';
import { useUserPermissions } from '@vivli/features/users/infrastructure/hook';
import { Size } from '@vivli/shared/theme';
import { AssetsConstant } from '@vivli/shared/infrastructure/constants';
import { useLocation, useNavigate } from 'react-router';
import { ExportCsvButtonView } from '@vivli/shared/views';

const containerStyle: CSSProperties = {
    height: '100%',
    padding: `0 ${Size.INSET_SIZE}px`
};

enum tabEnum {
    draft,
    inprogress,
    posted,
    cancelled
}

export const StudiesFeature = () => {
    const [inProgressStudies, setInProgressStudies] = useState<IStudy[]>();
    const [cancelledStudies, setCancelledStudies] = useState<IStudy[]>();
    const [draftStudies, setDraftStudies] = useState<IStudy[]>();
    const [postedStudies, setPostedStudies] = useState<IStudy[]>();
    const [activeTab, setActiveTab] = useState<number>(tabEnum.posted);
    const [cancelledGridApi, setCancelledGridApi] = useState<GridApi>(null);
    const [postedGridApi, setPostedGridApi] = useState<GridApi>(null);
    const [draftGridApi, setDraftGridApi] = useState<GridApi>(null);
    const [inProgressGridApi, setInProgressGridApi] = useState<GridApi>(null);
    const { isAmr } = useAssignedAppTypeContext();
    const { hasPermission } = useActiveUserContext();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const userPermissions = useUserPermissions();

    const { studies } = useStudies();

    //Confirmed with product owner that the rules for AMR and nonAMR are different
    const isVivliAdmin = hasPermission(PermissionsEnum.EditStudyAdv);
    const nonAdminAmrUser = isAmr && !isVivliAdmin;
    const showTitle = nonAdminAmrUser || (!isAmr && !isVivliAdmin && !userPermissions.isOrgOrVivliAdmin) ? null : `Add ${t('study')}`;
    const optionAction = [
        {
            title: `Single ${t('study')}`,
            onClick: () => navigate('/admin/studies/submitStudy')
        },
        {
            title: `Multiple ${t('studies')}`,
            onClick: () => navigate('/admin/bulkUploadStudies/true')
        },
        {
            title: `Update ${t('studies')}`,
            onClick: () => navigate('/admin/bulkUpdateStudies')
        }
    ];
    const showOptions = isAmr && !isVivliAdmin ? null : optionAction;

    const setTabFromRoute = () => {
        const currentPath = location.pathname;
        const routePath = currentPath.replace('/admin/studies/', '')?.toLowerCase();

        switch (routePath) {
            case 'draft':
                setActiveTab(tabEnum.draft);
                break;
            case 'inprogress':
                setActiveTab(tabEnum.inprogress);
                break;
            case 'cancelled':
                setActiveTab(tabEnum.cancelled);
                break;
            default:
                //no tab in the route, or one we don't recognize, lands on Posted
                setActiveTab(tabEnum.posted);
                break;
        }
    };

    const getSubheader = () => {
        //TODO PERMFE - redo with permissions+
        return 'All studies from my organization drafted, submitted, listed or cancelled on the Vivli platform';
    };

    const exportColumnKeys: string[] = [
        'studyTitle',
        'studyStatusDisplay',
        'dateDisplay',
        'sponsorProtocolId',
        'nctId',
        'hasSubmittedIpdString',
        'studyMetadataDoi'
    ];

    const exportToCsv = (tab: tabEnum) => {
        const dateStr = new Date().toLocaleDateString().replace('/', '_');
        if (isVivliAdmin && exportColumnKeys.indexOf('orgName') === -1) {
            exportColumnKeys.push('orgName');
        }
        switch (tab) {
            case tabEnum.cancelled:
                cancelledGridApi.exportDataAsCsv({
                    fileName: `Cancelled_Studies_${dateStr}`,
                    columnKeys: exportColumnKeys
                });
                break;
            case tabEnum.posted:
                postedGridApi.exportDataAsCsv({
                    fileName: `Posted_Studies_${dateStr}`,
                    columnKeys: exportColumnKeys
                });
                break;
            case tabEnum.inprogress:
                inProgressGridApi.exportDataAsCsv({
                    fileName: `InProgress_Studies_${dateStr}`,
                    columnKeys: exportColumnKeys
                });
                break;
            case tabEnum.draft:
                draftGridApi.exportDataAsCsv({
                    fileName: `Draft_Studies_${dateStr}`,
                    columnKeys: exportColumnKeys
                });
                break;
        }
    };

    const getExportButton = (te: tabEnum) => {
        const exportBtnStyle: CSSProperties = {
            position: 'absolute',
            right: !!showTitle ? 130 : 0
        };

        return <ExportCsvButtonView style={exportBtnStyle} onClick={() => exportToCsv(te)} />;
    };

    useEffect(() => {
        setTabFromRoute();
    }, [location.pathname]);

    useEffect(() => {
        if (!studies) {
            return;
        }

        const postedStudies = studies.filter((s) => s.status === StudyStatusEnum.Posted);
        const draftStudies = studies.filter((s) => s.status === StudyStatusEnum.Draft);
        const cancelledStudies = studies.filter((s) => s.status === StudyStatusEnum.Cancelled);
        const inProgressStudies = studies.filter(
            (s) => s.status !== StudyStatusEnum.Cancelled && s.status !== StudyStatusEnum.Draft && s.status !== StudyStatusEnum.Posted
        );

        setPostedStudies(postedStudies);
        setDraftStudies(draftStudies);
        setCancelledStudies(cancelledStudies);
        setInProgressStudies(inProgressStudies);
    }, [studies]);

    return (
        <div style={containerStyle}>
            <TabsComponent
                defaultTab={activeTab}
                commandImage={AssetsConstant.PLUS_SYMBOL}
                commandTitle={showTitle}
                commandOnClick={() => {
                    if (!isVivliAdmin) {
                        navigate('/admin/studies/submitStudy');
                    }
                }}
                commandOptions={showOptions}
                title={t('studies')}
                subTitle={getSubheader()}
            >
                <TabComponent
                    title={StudyTabEnum.Draft}
                    count={draftStudies?.length}
                    onTabClick={() => {
                        navigate('/admin/studies/draft');
                    }}
                    rightHeaderContent={getExportButton(tabEnum.draft)}
                >
                    <StudiesGridComponent type="Drafted" studies={draftStudies} onGridReady={setDraftGridApi} />
                </TabComponent>
                <TabComponent
                    title={StudyTabEnum.InProgress}
                    count={inProgressStudies?.length}
                    hint={'Studies awaiting curation'}
                    onTabClick={() => {
                        navigate('/admin/studies/inprogress');
                    }}
                    rightHeaderContent={getExportButton(tabEnum.inprogress)}
                >
                    <StudiesGridComponent type="Submitted" studies={inProgressStudies} onGridReady={setInProgressGridApi} />
                </TabComponent>
                <TabComponent
                    title={StudyTabEnum.Posted}
                    count={postedStudies?.length}
                    onTabClick={() => {
                        navigate('/admin/studies/posted');
                    }}
                    rightHeaderContent={getExportButton(tabEnum.posted)}
                >
                    <StudiesGridComponent type="Posted" studies={postedStudies} onGridReady={setPostedGridApi} />
                </TabComponent>
                <TabComponent
                    title={StudyTabEnum.Cancelled}
                    count={cancelledStudies?.length}
                    onTabClick={() => {
                        navigate('/admin/studies/cancelled');
                    }}
                    rightHeaderContent={getExportButton(tabEnum.cancelled)}
                >
                    <StudiesGridComponent type="Drafted" studies={cancelledStudies} onGridReady={setCancelledGridApi} />
                </TabComponent>
            </TabsComponent>
        </div>
    );
};
